import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share, Plus, Download, Smartphone } from 'lucide-react';
import DismissButton from '@/components/ui/DismissButton';
import {
  ACCENT, FONT_DISPLAY, FONT_BODY, FONT_MONO, TEXT, HAIRLINE, glow, microLabel,
} from '@/styles/premium';

const MobileInstallBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [browserType, setBrowserType] = useState<'safari' | 'chrome' | 'firefox' | 'android' | 'other'>('other');

  useEffect(() => {
    const checkStandalone = () => {
      return window.matchMedia('(display-mode: standalone)').matches || 
             (window.navigator as any).standalone === true;
    };

    const checkMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    const detectBrowser = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      
      if (userAgent.includes('android')) {
        if (userAgent.includes('chrome')) {
          return 'android';
        } else if (userAgent.includes('firefox')) {
          return 'firefox';
        } else {
          return 'android';
        }
      }
      
      if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
        return 'safari';
      }
      
      if (userAgent.includes('chrome')) {
        return 'chrome';
      }
      
      if (userAgent.includes('firefox')) {
        return 'firefox';
      }
      
      return 'other';
    };

    setIsStandalone(checkStandalone());
    setIsMobile(checkMobile());
    setBrowserType(detectBrowser());

    if (checkMobile() && !checkStandalone()) {
      const dismissed = sessionStorage.getItem('mobile-install-banner-dismissed');
      const lastDismissed = localStorage.getItem('mobile-install-banner-last-dismissed');
      const now = Date.now();
      
      if (!dismissed && (!lastDismissed || now - parseInt(lastDismissed) > 24 * 60 * 60 * 1000)) {
        setTimeout(() => setShowBanner(true), 2000);
      }
    }
  }, []);

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem('mobile-install-banner-dismissed', 'true');
    localStorage.setItem('mobile-install-banner-last-dismissed', Date.now().toString());
  };

  const getInstallInstructions = () => {
    switch (browserType) {
      case 'safari':
        return {
          icon: Share,
          steps: [
            'Tap the Share button',
            'Scroll down and tap "Add to Home Screen"',
            'Tap "Add" to install the app'
          ],
          platform: 'iOS Safari'
        };
      case 'android':
        return {
          icon: Plus,
          steps: [
            'Tap the menu (⋮) or "Install App" banner',
            'Select "Add to Home screen" or "Install"',
            'Confirm by tapping "Add" or "Install"'
          ],
          platform: 'Android Chrome'
        };
      case 'chrome':
        return {
          icon: Plus,
          steps: [
            'Tap the menu (⋮) button',
            'Tap "Add to Home screen"',
            'Tap "Add" to install the app'
          ],
          platform: 'Chrome'
        };
      case 'firefox':
        return {
          icon: Plus,
          steps: [
            'Tap the menu button',
            'Tap "Install"',
            'Follow the prompts to install'
          ],
          platform: 'Firefox'
        };
      default:
        return {
          icon: Download,
          steps: [
            'Look for "Install" or "Add to Home Screen"',
            'Usually in the browser menu',
            'Follow the installation prompts'
          ],
          platform: 'Mobile Browser'
        };
    }
  };

  if (!isMobile || isStandalone || !showBanner) {
    return null;
  }

  const instructions = getInstallInstructions();
  const Icon = instructions.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="fixed left-0 right-0 p-3 sm:p-4 lg:left-auto lg:right-4 lg:!bottom-4 lg:max-w-md"
        style={{ 
          zIndex: 9999, // Much higher than bottom nav's z-50
          bottom: 'calc(5rem + env(safe-area-inset-bottom))' // 80px + safe area
        }}
      >
        <div
          className="max-h-[80vh] overflow-y-auto"
          style={{
            background: 'linear-gradient(135deg,rgba(16,16,16,.97) 0%,rgba(10,10,10,.97) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(20,184,166,.18)',
            borderRadius: 18,
            padding: 'clamp(16px,4.5vw,22px)',
            fontFamily: FONT_BODY,
            color: TEXT.primary,
            boxShadow: '0 18px 50px rgba(0,0,0,.55)',
            position: 'relative',
          }}
        >
          <div style={{ ...glow(200, 0.14), top: -80, right: -60 }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
              <div
                style={{
                  width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                  background: 'rgba(20,184,166,.15)',
                  border: '1px solid rgba(20,184,166,.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Smartphone size={18} color={ACCENT} aria-hidden />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...microLabel, marginBottom: 4 }}>Add to home screen</div>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 'clamp(19px,5vw,22px)', fontWeight: 400, letterSpacing: '-.01em', lineHeight: 1.1 }}>
                  Install UniSchedule
                </div>
              </div>
              <DismissButton onClick={handleDismiss} />
            </div>

            <p style={{ fontSize: 12.5, lineHeight: 1.65, color: TEXT.muted, fontWeight: 300, marginBottom: 18 }}>
              Faster access, works offline, and opens like a native app.
            </p>

            {/* Install instructions */}
            <div
              style={{
                background: 'rgba(255,255,255,.03)',
                border: `1px solid ${HAIRLINE}`,
                borderRadius: 14,
                padding: 'clamp(14px,3.5vw,16px)',
                marginBottom: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Icon size={13} color={ACCENT} aria-hidden style={{ flexShrink: 0 }} />
                <span style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '.12em', color: 'rgba(20,184,166,.7)', textTransform: 'uppercase' }}>
                  {instructions.platform}
                </span>
              </div>
              <ol style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: 0, padding: 0, listStyle: 'none' }}>
                {instructions.steps.map((step, index) => (
                  <li key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span
                      style={{
                        width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                        background: 'rgba(20,184,166,.12)',
                        border: '1px solid rgba(20,184,166,.22)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: FONT_MONO, fontSize: 9, color: ACCENT,
                      }}
                    >
                      {index + 1}
                    </span>
                    <span style={{ fontSize: 12.5, lineHeight: 1.55, color: TEXT.secondary, fontWeight: 300 }}>
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Platform-specific hint */}
            {(browserType === 'safari' || browserType === 'android') && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 8, flexWrap: 'wrap', marginBottom: 16,
                  fontSize: 11.5, color: TEXT.faint, fontWeight: 300,
                }}
              >
                <span>Look for</span>
                <span
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'rgba(20,184,166,.1)',
                    border: '1px solid rgba(20,184,166,.2)',
                    borderRadius: 7, padding: '3px 8px', color: ACCENT,
                  }}
                >
                  {browserType === 'safari'
                    ? <Share size={12} aria-hidden />
                    : <span style={{ fontFamily: FONT_MONO, fontSize: 12, lineHeight: 1 }}>⋮</span>}
                </span>
                <span>{browserType === 'safari' ? 'at the bottom' : 'in the top right'}</span>
              </div>
            )}

            {/* Benefits */}
            <div
              style={{
                display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 14,
                borderTop: `1px solid ${HAIRLINE}`, paddingTop: 14,
              }}
            >
              {['Offline access', 'Faster loading', 'Native feel'].map(label => (
                <div
                  key={label}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '.1em',
                    color: TEXT.faint, textTransform: 'uppercase',
                  }}
                >
                  <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(20,184,166,.7)' }} />
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MobileInstallBanner;
